import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, FileText, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ErrorMessage } from '@/components/ui/error-message';
import { Input } from '@/components/ui/input';
import { ProvenanceChip } from '@/components/ui/provenance-chip';
import { Spinner } from '@/components/ui/spinner';
import {
  useContractFactsQuery,
  useSetContractFactMutation,
  type ContractFact,
} from '@/lib/api/contract-facts';
import { formatDay } from './checkpoint-status';

/**
 * Contract facts — the contract-level terms of the engagement: vendor,
 * value, dates and the commercial protections.
 *
 * Each fact is read from the contract by AI and confirmed in the review
 * queue (shown with the document and page it came from), or typed here by a
 * person. Anyone on the engagement can read them; an engagement lead can
 * set, correct or clear one. Facts the contract has not yielded yet show as
 * "Not found yet" so the gaps are visible.
 */
export function ContractFactsCard({ engagementId }: { engagementId: string }) {
  const facts = useContractFactsQuery(engagementId);
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <Card aria-labelledby="contract-facts-title">
      <CardContent className="p-0">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-ink-100 px-5 py-3">
          <div>
            <h2
              id="contract-facts-title"
              className="flex items-center gap-2 text-h3 font-semibold text-ink-900"
            >
              <FileText className="h-4 w-4" aria-hidden="true" />
              Contract facts
            </h2>
            <p className="text-meta text-ink-500">
              Read from the contract and confirmed in the review queue, or entered by hand.
            </p>
          </div>
          {facts.data && facts.data.pending_proposals > 0 ? (
            <Link
              to="/engagements/$engagementId/proposals"
              params={{ engagementId }}
              className="text-body-sm font-medium text-accent-amber-700 underline-offset-2 hover:underline"
            >
              {facts.data.pending_proposals} waiting for review
            </Link>
          ) : null}
        </div>
        {facts.isLoading ? (
          <div className="flex items-center gap-2 px-5 py-4 text-body-sm text-ink-500">
            <Spinner /> Loading contract facts…
          </div>
        ) : facts.error ? (
          <div className="px-5 py-4">
            <ErrorMessage error={facts.error} />
          </div>
        ) : (
          <dl className="grid grid-cols-1 divide-y divide-ink-100 md:grid-cols-2 md:divide-y-0">
            {(facts.data?.facts ?? []).map((fact) => (
              <FactRow
                key={fact.fact_key}
                engagementId={engagementId}
                fact={fact}
                editing={editing === fact.fact_key}
                onEdit={() => setEditing(fact.fact_key)}
                onDone={() => setEditing(null)}
              />
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function FactRow({
  engagementId,
  fact,
  editing,
  onEdit,
  onDone,
}: {
  engagementId: string;
  fact: ContractFact;
  editing: boolean;
  onEdit: () => void;
  onDone: () => void;
}) {
  const setFact = useSetContractFactMutation(engagementId);
  const [draft, setDraft] = useState(fact.value ?? '');

  function save() {
    const value = draft.trim();
    setFact.mutate(
      { factKey: fact.fact_key, value: value.length > 0 ? value : null },
      { onSuccess: onDone },
    );
  }

  return (
    <div className="border-ink-100 px-5 py-3 md:border-b">
      <dt className="flex items-center justify-between gap-2 text-meta font-semibold uppercase tracking-wider text-ink-500">
        {fact.label}
        {!editing ? (
          <button
            type="button"
            onClick={() => {
              setDraft(fact.value ?? '');
              onEdit();
            }}
            className="rounded p-0.5 text-ink-500 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
            aria-label={`Edit ${fact.label}`}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </dt>
      <dd className="mt-1 text-body-sm">
        {editing ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input
                type={fact.kind === 'date' ? 'date' : 'text'}
                value={draft}
                onChange={(ev) => setDraft(ev.target.value)}
                aria-label={fact.label}
                autoFocus
              />
              <Button type="button" size="sm" onClick={save} disabled={setFact.isPending}>
                <Check className="h-4 w-4" aria-hidden="true" />
                Save
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={onDone}>
                <X className="h-4 w-4" aria-hidden="true" />
                Cancel
              </Button>
            </div>
            <p className="text-meta text-ink-500">Leave it empty and save to clear this fact.</p>
            {setFact.error ? <ErrorMessage error={setFact.error} /> : null}
          </div>
        ) : fact.value ? (
          <>
            <span className="text-ink-900">
              {fact.kind === 'date' ? formatDay(fact.value) : fact.value}
            </span>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-meta text-ink-500">
              <ProvenanceChip origin={fact.source === 'manual' ? 'manual' : 'ai_confirmed'} />
              {fact.document_title ? (
                <span>
                  {fact.document_title}
                  {fact.page_number ? `, p.${fact.page_number}` : ''}
                </span>
              ) : null}
            </div>
          </>
        ) : (
          <span className="text-ink-500">Not found yet</span>
        )}
      </dd>
    </div>
  );
}
